import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_URL,
  // Send / accept the httpOnly auth cookie. Bearer header still works for
  // legacy clients during the iter-42 hybrid migration.
  withCredentials: true,
});

// Cached CSRF token. Populated by ensureCsrf() on first state-changing call
// or by the initial /auth/csrf fetch at app boot.
let _csrfToken = null;

// Read the readable CSRF cookie (set by GET /api/auth/csrf or login response).
const _readCsrfCookie = () => {
  const m = document.cookie.match(/(?:^|;\s*)tin_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

export const ensureCsrf = async () => {
  if (_csrfToken) return _csrfToken;
  const fromCookie = _readCsrfCookie();
  if (fromCookie) {
    _csrfToken = fromCookie;
    return _csrfToken;
  }
  try {
    const res = await axios.get(`${API_URL}/auth/csrf`, { withCredentials: true });
    _csrfToken = res.data?.csrf_token || _readCsrfCookie();
  } catch (e) {
    // If unreachable we still try cookie one more time
    _csrfToken = _readCsrfCookie();
  }
  return _csrfToken;
};

// Simple in-memory cache
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache helper
const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Add Bearer token (legacy) + CSRF header on state-changing requests.
api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = await ensureCsrf();
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

// Add response caching for GET requests
api.interceptors.response.use((response) => {
  if (response.config.method === 'get' && !response.config.params?.skipCache) {
    const cacheKey = `${response.config.url}_${JSON.stringify(response.config.params || {})}`;
    setCachedData(cacheKey, response.data);
  }
  return response;
});

// Enhanced GET with cache
const cachedGet = async (url, config = {}) => {
  const cacheKey = `${url}_${JSON.stringify(config.params || {})}`;
  const cached = getCachedData(cacheKey);

  if (cached && !config.skipCache) {
    return { data: cached };
  }

  return api.get(url, config);
};

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => cachedGet('/auth/me'),
};

// Profile API
export const profileAPI = {
  getStaffProfile: (userId) => cachedGet(`/profile/staff/${userId}`),
  getVenueProfile: (userId) => cachedGet(`/profile/venue/${userId}`),
  updateStaffProfile: (data) => api.put('/profile/staff', data),
  updateVenueProfile: (data) => api.put('/profile/venue', data),
};

// Resume & Portfolio API
export const portfolioAPI = {
  uploadResume: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/resume', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadPortfolioImage: async (file, caption = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', caption);
    return api.post('/upload/portfolio-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePortfolioImage: (imageUrl) =>
    api.delete('/upload/portfolio-image', { params: { image_url: imageUrl } }),
};

// Timeline API
export const timelineAPI = {
  getTimeline: (userId, limit = 50) => cachedGet(`/timeline/${userId}`, { params: { limit } }),
  createEntry: (data) => api.post('/timeline', data),
  updateEntry: (entryId, data) => api.put(`/timeline/${entryId}`, data),
  deleteEntry: (entryId) => api.delete(`/timeline/${entryId}`),
};

// Staff Feed API
export const feedAPI = {
  getStaffFeed: (params) => cachedGet('/feed/staff', { params }),
};

// Badges API
export const badgeAPI = {
  awardBadge: (data) => api.post('/badges', data),
  getUserBadges: (userId) => cachedGet(`/badges/${userId}`),
  getBadgeTypes: () => cachedGet('/badges/types/all'),
};

// Availability Calendar API
export const availabilityAPI = {
  setAvailability: (data) => api.post('/availability', data),
  setBulkAvailability: (data) => api.post('/availability/bulk', data),
  getUserAvailability: (userId, startDate, endDate) =>
    cachedGet(`/availability/${userId}`, { params: { start_date: startDate, end_date: endDate } }),
  deleteAvailability: (date) => api.delete('/availability', { params: { date } }),
};

// Video Intro API
export const videoIntroAPI = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/video-intro', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getIntro: (userId) => cachedGet(`/video-intro/${userId}`),
  deleteIntro: () => api.delete('/video-intro'),
};

// Referral API
export const referralAPI = {
  getStats: () => cachedGet('/referrals/me'),
  getLeaderboard: () => cachedGet('/referrals/leaderboard'),
};

// Shift Swap API
export const shiftsAPI = {
  list: (params) => api.get('/shifts/swaps', { params }),
  mine: () => api.get('/shifts/swaps/mine'),
  history: () => api.get('/shifts/swaps/history'),
  get: (id) => api.get(`/shifts/swaps/${id}`),
  create: (body) => api.post('/shifts/swaps', body),
  claim: (id) => api.post(`/shifts/swaps/${id}/claim`),
  cancelClaim: (id) => api.post(`/shifts/swaps/${id}/cancel-claim`),
  cancel: (id) => api.post(`/shifts/swaps/${id}/cancel`),
  venueDecision: (id, body) => api.post(`/shifts/swaps/${id}/venue-decision`, body),
  autoMatch: (id) => api.get(`/shifts/swaps/${id}/auto-match`),
  messages: (id) => api.get(`/shifts/swaps/${id}/messages`),
  sendMessage: (id, content) => api.post(`/shifts/swaps/${id}/messages`, { content }),
  adminOverride: (id, body) => api.post(`/admin/shifts/swaps/${id}/override`, body),
};

// Job Highlighting API
export const highlightAPI = {
  highlightJob: (jobId, data) => api.post(`/jobs/${jobId}/highlight`, data),
  removeHighlight: (jobId) => api.delete(`/jobs/${jobId}/highlight`),
};

// Email Subscription API
export const subscriptionAPI = {
  subscribe: (data) => api.post('/subscriptions/email', data),
  getSubscriptions: () => cachedGet('/subscriptions/email'),
  unsubscribe: (type) => api.delete(`/subscriptions/email/${type}`),
};

// Advanced Search API
export const searchAPI = {
  searchJobs: (params) => cachedGet('/jobs/search/advanced', { params }),
  searchStaff: (params) => cachedGet('/staff/search/advanced', { params }),
  nearbyJobs: (lat, lng, radius) =>
    cachedGet('/jobs/nearby', { params: { lat, lng, radius_miles: radius } }),
};

// Push Notifications API
export const pushAPI = {
  subscribe: (subscription) => api.post('/notifications/push/subscribe', { subscription }),
  unsubscribe: () => api.delete('/notifications/push/unsubscribe'),
};

// Job API
export const jobAPI = {
  createJob: (data) => api.post('/jobs', data),
  getJobs: (params) => cachedGet('/jobs', { params }),
  getJob: (jobId) => cachedGet(`/jobs/${jobId}`),
  getVenueJobs: (venueId) => cachedGet(`/jobs/venue/${venueId}`),
  applyToJob: (jobId, data) => api.post(`/jobs/${jobId}/apply`, data),
  getStaffApplications: () => cachedGet('/applications/staff'),
  getJobApplications: (jobId) => cachedGet(`/applications/job/${jobId}`),
};

// Staff API
export const staffAPI = {
  browseStaff: (params) => cachedGet('/staff/browse', { params }),
};

// Marketplace API
export const marketplaceAPI = {
  createListing: (data) => api.post('/marketplace', data),
  getListings: (params) => cachedGet('/marketplace', { params }),
  getListing: (listingId) => cachedGet(`/marketplace/${listingId}`),
  getUserListings: (userId) => cachedGet(`/marketplace/user/${userId}`),
};

// Review API
export const reviewAPI = {
  createReview: (data) => api.post('/reviews', data),
  getUserReviews: (userId) => cachedGet(`/reviews/${userId}`),
};

// Message API
export const messageAPI = {
  sendMessage: (data) => api.post('/messages', data),
  getConversations: () => cachedGet('/messages/conversations', { skipCache: true }),
  getMessagesWithUser: (userId) => cachedGet(`/messages/${userId}`, { skipCache: true }),
  getUnreadCount: () => api.get('/messages/unread/count'),
  markThreadRead: (otherUserId) => api.put(`/messages/${otherUserId}/read`),
  markAllRead: () => api.put('/messages/mark-all-read'),
};

// Notification API
export const notificationAPI = {
  getNotifications: () => cachedGet('/notifications', { skipCache: true }),
  getUnreadCount: () => api.get('/notifications/unread/count'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
};

// User preferences (cross-device sync of UI toggles)
export const userPreferencesAPI = {
  get: () => api.get('/user/preferences'),
  put: (preferences) => api.put('/user/preferences', { preferences }),
};

// ==================== SOCIAL FEATURES API ====================

// Activity Feed API
export const activityAPI = {
  getFeed: (params) => cachedGet('/feed', { params, skipCache: true }),
  postToFeed: (data) => api.post('/feed/post', data),
};

// Follow System API
export const followAPI = {
  follow: (userId) => api.post(`/follow/${userId}`),
  unfollow: (userId) => api.delete(`/follow/${userId}`),
  getFollowers: (userId, params) => cachedGet(`/followers/${userId}`, { params }),
  getFollowing: (userId, params) => cachedGet(`/following/${userId}`, { params }),
  checkFollowStatus: (userId) => cachedGet(`/follow/check/${userId}`, { skipCache: true }),
};

// Connections API (Staff-to-Staff)
export const connectionAPI = {
  sendRequest: (userId, data) => api.post(`/connections/request/${userId}`, data),
  respond: (connectionId, action) => api.post(`/connections/respond/${connectionId}`, { action }),
  getConnections: () => cachedGet('/connections', { skipCache: true }),
  getPending: () => cachedGet('/connections/pending', { skipCache: true }),
  getMutual: (userId) => cachedGet(`/connections/mutual/${userId}`),
};

// Quick Status API
export const statusAPI = {
  setStatus: (data) => api.post('/status', data),
  getStatus: (userId) => cachedGet(`/status/${userId}`, { skipCache: true }),
  clearStatus: () => api.delete('/status'),
  getActiveStatuses: () => cachedGet('/status/active', { skipCache: true }),
};

// Events API
export const eventAPI = {
  createEvent: (data) => api.post('/events', data),
  getEvents: (params) => cachedGet('/events', { params }),
  getEvent: (eventId) => cachedGet(`/events/${eventId}`),
  rsvp: (eventId, status) => api.post(`/events/${eventId}/rsvp`, { status }),
  getAttendees: (eventId) => cachedGet(`/events/${eventId}/attendees`),
};

// Group Chat API
export const groupAPI = {
  createGroup: (data) => api.post('/groups', data),
  getGroups: (params) => cachedGet('/groups', { params }),
  getMyGroups: () => cachedGet('/groups/my', { skipCache: true }),
  joinGroup: (groupId) => api.post(`/groups/${groupId}/join`),
  leaveGroup: (groupId) => api.post(`/groups/${groupId}/leave`),
  getMessages: (groupId, params) =>
    cachedGet(`/groups/${groupId}/messages`, { params, skipCache: true }),
  sendMessage: (groupId, data) => api.post(`/groups/${groupId}/messages`, data),
};

// Spotlight API
export const spotlightAPI = {
  getCurrent: () => cachedGet('/spotlight/current'),
  getHistory: (limit) => cachedGet('/spotlight/history', { params: { limit } }),
  nominate: (data) => api.post('/spotlight/nominate', data),
};

// Comments & Reactions API
export const socialAPI = {
  addComment: (data) => api.post('/comments', data),
  getComments: (parentType, parentId, params) =>
    cachedGet(`/comments/${parentType}/${parentId}`, { params }),
  deleteComment: (commentId) => api.delete(`/comments/${commentId}`),
  addReaction: (data) => api.post('/reactions', data),
  getReactions: (parentType, parentId) => cachedGet(`/reactions/${parentType}/${parentId}`),
};
